import {
  PaymentInfo,
  PaymentMethods,
  PaymentActivityResponse,
  AutoPaymentInfo,
} from "../mocks/Payments.mock";
import { API_BASE } from "../shared/config/config";

// Payments
export async function getPaymentActivity(
  accessToken,
  impersonatedUser,
  loanId,
  noOfPages,
  pageSize,
  skipToken,
) {
  if (
    process.env.REACT_APP_API_INTEGRATION === "0" &&
    (!process.env.NODE_ENV || process.env.NODE_ENV === "development")
  ) {
    return new Promise((resolve, reject) => resolve(PaymentActivityResponse));
  } else {
    const headers = new Headers();
    const bearer = `Bearer ${accessToken}`;
    headers.append("Authorization", bearer);

    if (impersonatedUser) {
      headers.append("X-Impersonated-User", impersonatedUser);
    }

    const options = {
      method: "GET",
      headers: headers,
    };

    const queryParamString = `?loanId=${loanId}${
      pageSize !== null
        ? `&pageSize=${pageSize}`
        : `&skipToken=${skipToken}${noOfPages > 1 ? `&pages=${noOfPages}` : ""}`
    }`;

    return fetch(`${API_BASE}/payments${queryParamString}`, options)
      .then((response) => response.json())
      .catch((error) => console.log(error));
  }
}

export async function postPayment(accessToken, body, impersonatedUser) {
  if (
    process.env.REACT_APP_API_INTEGRATION === "0" &&
    (!process.env.NODE_ENV || process.env.NODE_ENV === "development")
  ) {
    return new Promise((resolve, reject) => resolve(""));
  } else {
    const headers = new Headers();
    const bearer = `Bearer ${accessToken}`;

    headers.append("Authorization", bearer);
    headers.append("Content-Type", "application/json");

    if (impersonatedUser) {
      headers.append("X-Impersonated-User", impersonatedUser);
    }

    const options = {
      method: "POST",
      headers: headers,
      body: JSON.stringify(body),
    };

    return fetch(`${API_BASE}/payments`, options)
      .then((response) => response.json())
      .catch((error) => console.log(error));
  }
}

/**
 * Ask the payments service what this payment would cost, without making it.
 *
 * Runs the same validation chain the execute call runs, so the quoted fee is the fee
 * that will be charged. The portal used to compute this itself, which drifted from the
 * service in five separate ways: the posting date the service advances past cut-off
 * times and holidays, the timezone the day count is taken in, the threshold boundary,
 * the percentage rounding mode, and the padding of the fee-waiver routing number.
 *
 * Unlike its neighbours this rejects rather than swallowing failures. A quote that
 * fails quietly leaves the previous amount on screen, and a stale transaction fee is a
 * number the borrower agrees to and is not charged.
 */
export async function postPaymentQuote(accessToken, body, impersonatedUser) {
  if (
    process.env.REACT_APP_API_INTEGRATION === "0" &&
    (!process.env.NODE_ENV || process.env.NODE_ENV === "development")
  ) {
    return Promise.resolve({
      processingFeesAmount: 0,
      postingDate: body?.postingDate,
    });
  }

  const headers = new Headers();
  const bearer = `Bearer ${accessToken}`;

  headers.append("Authorization", bearer);
  headers.append("Content-Type", "application/json");

  if (impersonatedUser) {
    headers.append("X-Impersonated-User", impersonatedUser);
  }

  const options = {
    method: "POST",
    headers: headers,
    body: JSON.stringify(body),
  };

  const response = await fetch(`${API_BASE}/payments/quote`, options);
  if (!response.ok) {
    const error = new Error(
      `payment quote failed with status ${response.status}`,
    );

    // A 400 is the service rejecting the payment rather than the call failing, and the
    // message it returns is written for the borrower: over the state transaction
    // maximum, too delinquent, and so on. Quoting runs the same validation chain as
    // submitting, so these are the messages the borrower used to see on submit and
    // still needs. Any other status means the request did not get an answer, which
    // tells them nothing.
    if (response.status === 400) {
      error.validationMessage = await response
        .json()
        .then((errorBody) => errorBody?.message ?? "")
        .catch(() => "");
    }

    throw error;
  }

  return response.json();
}

export async function getPaymentInfo(accessToken, loanId, impersonatedUser) {
  if (
    process.env.REACT_APP_API_INTEGRATION === "0" &&
    (!process.env.NODE_ENV || process.env.NODE_ENV === "development")
  ) {
    return new Promise((resolve, reject) => resolve(PaymentInfo));
  } else {
    const headers = new Headers();
    const bearer = `Bearer ${accessToken}`;
    const body = {
      loanId,
    };

    headers.append("Authorization", bearer);
    headers.append("Content-Type", "application/json");

    if (impersonatedUser) {
      headers.append("X-Impersonated-User", impersonatedUser);
    }

    const options = {
      method: "POST",
      headers: headers,
      body: JSON.stringify(body),
    };

    return fetch(`${API_BASE}/payments/prevalidate`, options)
      .then((response) => response.json())
      .catch((error) => console.log(error));
  }
}

export async function deletePayment(
  accessToken,
  confirmationNumber,
  loanId,
  impersonatedUser,
) {
  if (
    process.env.REACT_APP_API_INTEGRATION === "0" &&
    (!process.env.NODE_ENV || process.env.NODE_ENV === "development")
  ) {
    return new Promise((resolve, reject) => resolve(PaymentMethods));
  } else {
    const headers = new Headers();
    const bearer = `Bearer ${accessToken}`;

    headers.append("Authorization", bearer);
    headers.append("Content-Type", "application/json");

    if (impersonatedUser) {
      headers.append("X-Impersonated-User", impersonatedUser);
    }

    const options = {
      method: "DELETE",
      headers: headers,
    };

    return fetch(
      `${API_BASE}/payments/${confirmationNumber}?loanId=${loanId}`,
      options,
    )
      .then((response) => response.json())
      .catch((error) => console.log(error));
  }
}

// Auto-Payments
export async function getAutoPaymentInfo(
  accessToken,
  loanId,
  impersonatedUser,
) {
  if (
    process.env.REACT_APP_API_INTEGRATION === "0" &&
    (!process.env.NODE_ENV || process.env.NODE_ENV === "development")
  ) {
    return new Promise((resolve, reject) => resolve(AutoPaymentInfo));
  } else {
    const headers = new Headers();
    const bearer = `Bearer ${accessToken}`;
    const body = {
      loanId,
    };

    headers.append("Authorization", bearer);
    headers.append("Content-Type", "application/json");

    if (impersonatedUser) {
      headers.append("X-Impersonated-User", impersonatedUser);
    }

    const options = {
      method: "POST",
      headers: headers,
      body: JSON.stringify(body),
    };

    return fetch(`${API_BASE}/autoPayments/prevalidate`, options)
      .then((response) => response.json())
      .catch((error) => console.log(error));
  }
}

export async function postRecurringPayment(
  accessToken,
  body,
  impersonatedUser,
) {
  if (
    process.env.REACT_APP_API_INTEGRATION === "0" &&
    (!process.env.NODE_ENV || process.env.NODE_ENV === "development")
  ) {
    return new Promise((resolve, reject) => resolve(""));
  } else {
    const headers = new Headers();
    const bearer = `Bearer ${accessToken}`;

    headers.append("Authorization", bearer);
    headers.append("Content-Type", "application/json");

    if (impersonatedUser) {
      headers.append("X-Impersonated-User", impersonatedUser);
    }

    const options = {
      method: "POST",
      headers: headers,
      body: JSON.stringify(body),
    };

    return fetch(`${API_BASE}/autoPayments`, options)
      .then((response) => response.json())
      .catch((error) => console.log(error));
  }
}

export async function putRecurringPayment(
  accessToken,
  loanId,
  body,
  impersonatedUser,
) {
  if (
    process.env.REACT_APP_API_INTEGRATION === "0" &&
    (!process.env.NODE_ENV || process.env.NODE_ENV === "development")
  ) {
    return new Promise((resolve, reject) => resolve(""));
  } else {
    const headers = new Headers();
    const bearer = `Bearer ${accessToken}`;

    headers.append("Authorization", bearer);
    headers.append("Content-Type", "application/json");

    if (impersonatedUser) {
      headers.append("X-Impersonated-User", impersonatedUser);
    }

    const options = {
      method: "PUT",
      headers: headers,
      body: JSON.stringify(body),
    };

    return fetch(`${API_BASE}/autoPayments/${loanId}`, options)
      .then((response) => response.json())
      .catch((error) => console.log(error));
  }
}

export async function deleteRecurringPayment(
  accessToken,
  loanId,
  impersonatedUser,
) {
  if (
    process.env.REACT_APP_API_INTEGRATION === "0" &&
    (!process.env.NODE_ENV || process.env.NODE_ENV === "development")
  ) {
    return new Promise((resolve, reject) => resolve({ success: "true" }));
  } else {
    const headers = new Headers();
    const bearer = `Bearer ${accessToken}`;

    headers.append("Authorization", bearer);
    headers.append("Content-Type", "application/json");

    if (impersonatedUser) {
      headers.append("X-Impersonated-User", impersonatedUser);
    }

    const options = {
      method: "DELETE",
      headers: headers,
    };

    return fetch(`${API_BASE}/autoPayments/${loanId}`, options)
      .then((response) => response.json())
      .catch((error) => console.log(error));
  }
}

// Payment Methods
export async function getPaymentMethods(accessToken, impersonatedUser) {
  if (
    process.env.REACT_APP_API_INTEGRATION === "0" &&
    (!process.env.NODE_ENV || process.env.NODE_ENV === "development")
  ) {
    return new Promise((resolve, reject) => resolve(PaymentMethods));
  } else {
    const headers = new Headers();
    const bearer = `Bearer ${accessToken}`;

    headers.append("Authorization", bearer);

    if (impersonatedUser) {
      headers.append("X-Impersonated-User", impersonatedUser);
    }

    const options = {
      method: "GET",
      headers: headers,
    };

    return fetch(`${API_BASE}/paymentMethods`, options)
      .then(async (response) => {
        if (response.status === 204) {
          return [];
        }
        if (response.status === 200) {
          return response.json();
        }
        console.log(`getPaymentMethods failed with status ${response.status}`);
        return [];
      })
      .catch((error) => {
        console.log(error);
        return [];
      });
  }
}

export async function postPaymentMethod(accessToken, body, impersonatedUser) {
  if (
    process.env.REACT_APP_API_INTEGRATION === "0" &&
    (!process.env.NODE_ENV || process.env.NODE_ENV === "development")
  ) {
    return new Promise((resolve, reject) => resolve(""));
  } else {
    const headers = new Headers();
    const bearer = `Bearer ${accessToken}`;

    headers.append("Authorization", bearer);
    headers.append("Content-Type", "application/json");

    if (impersonatedUser) {
      headers.append("X-Impersonated-User", impersonatedUser);
    }

    const options = {
      method: "POST",
      headers: headers,
      body: JSON.stringify(body),
    };

    return fetch(`${API_BASE}/paymentMethods`, options)
      .then((response) => response.json())
      .catch((error) => console.log(error));
  }
}

export async function postPaymentMethodVerification(
  accessToken,
  body,
  impersonatedUser,
) {
  if (
    process.env.REACT_APP_API_INTEGRATION === "0" &&
    (!process.env.NODE_ENV || process.env.NODE_ENV === "development")
  ) {
    return new Promise((resolve, reject) => resolve(""));
  } else {
    const headers = new Headers();
    const bearer = `Bearer ${accessToken}`;

    headers.append("Authorization", bearer);
    headers.append("Content-Type", "application/json");

    if (impersonatedUser) {
      headers.append("X-Impersonated-User", impersonatedUser);
    }

    const options = {
      method: "POST",
      headers: headers,
      body: JSON.stringify(body),
    };

    return fetch(`${API_BASE}/paymentMethods/verify`, options)
      .then((response) => response.json())
      .catch((error) => console.log(error));
  }
}

export async function deletePaymentMethod(
  accessToken,
  nickname,
  impersonatedUser,
) {
  if (
    process.env.REACT_APP_API_INTEGRATION === "0" &&
    (!process.env.NODE_ENV || process.env.NODE_ENV === "development")
  ) {
    return new Promise((resolve, reject) => resolve(PaymentMethods));
  } else {
    const headers = new Headers();
    const bearer = `Bearer ${accessToken}`;

    headers.append("Authorization", bearer);
    headers.append("Content-Type", "application/json");

    if (impersonatedUser) {
      headers.append("X-Impersonated-User", impersonatedUser);
    }

    const options = {
      method: "DELETE",
      headers: headers,
    };

    return fetch(`${API_BASE}/paymentMethods/${nickname}`, options)
      .then((response) => response.json())
      .catch((error) => console.log(error));
  }
}

export async function putPaymentMethod(
  accessToken,
  nickname,
  body,
  impersonatedUser,
) {
  if (
    process.env.REACT_APP_API_INTEGRATION === "0" &&
    (!process.env.NODE_ENV || process.env.NODE_ENV === "development")
  ) {
    return new Promise((resolve, reject) => resolve(PaymentMethods));
  } else {
    const headers = new Headers();
    const bearer = `Bearer ${accessToken}`;

    headers.append("Authorization", bearer);
    headers.append("Content-Type", "application/json");

    if (impersonatedUser) {
      headers.append("X-Impersonated-User", impersonatedUser);
    }

    const options = {
      method: "PUT",
      headers: headers,
      body: JSON.stringify(body),
    };

    return fetch(`${API_BASE}/paymentMethods/${nickname}`, options)
      .then((response) => response.json())
      .catch((error) => console.log(error));
  }
}
